import { AiError } from './types'

/**
 * Helper para leer respuestas de streaming SSE de un `fetch` y extraer
 * eventos `data:` línea a línea. Las APIs de OpenAI, Anthropic y el
 * proxy usan todas este formato (NDJSON o SSE puro).
 *
 * Devuelve cada payload `data:` decodificado como string (sin el
 * prefijo `data: `). La línea `data: [DONE]` se ignora.
 */
export async function* readSseData(
  response: Response,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  if (!response.body) {
    throw new AiError('La respuesta no tiene cuerpo (streaming no soportado).')
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      if (signal?.aborted) {
        reader.cancel().catch(() => {})
        throw new AiError('Generación cancelada.', 499)
      }
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      // Procesar líneas completas (separadas por \n).
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const rawLine of lines) {
        const line = rawLine.trim()
        if (!line || line.startsWith(':')) continue // comentario keep-alive
        if (!line.startsWith('data:')) continue
        const data = line.slice(5).trim()
        if (data === '[DONE]') return
        yield data
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Lee el cuerpo de la respuesta como texto y lo devuelve.
 * Lanza AiError con el mensaje del servidor si el status no es 2xx.
 */
export async function readJsonOrThrow<T = unknown>(response: Response): Promise<T> {
  const text = await response.text()
  if (!response.ok) {
    let detail = text
    try {
      const parsed = JSON.parse(text)
      detail = parsed.error?.message ?? parsed.error ?? parsed.message ?? text
    } catch {
      /* mantener el texto plano */
    }
    throw new AiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      response.status,
    )
  }
  return JSON.parse(text) as T
}

/** Pequeño retardo para agrupar reflow en la UI (no usado en streaming crudo). */
export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}
